import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AllocationTableInPendingAction from '../allocation-table-in-pending-action';
import { IPendingActionTypeThreeData, ISuggestedAllocations } from '../../type';
import AvatarImage from '../../../common/avatar-image';
import PendingActionButtonsComponent from '../../../common/widgets/PendingActionButtonsComponent';
import SendIcon from '@mui/icons-material/Send';
import { ApprovalRequestType } from '../../../common/utils/enum';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { EDIT_MEMBER_APPROVAL_REQUEST } from '../../gql';
import { useAppSelector } from '../../../../redux/hooks';

interface IPendingActionCardProps {
  cardData: IPendingActionTypeThreeData;
}

function PendingActionCardTypeThree({
  cardData
}: IPendingActionCardProps): JSX.Element {
  const [isRejected, setIsRejected] = useState(false);
  const [comment, setComment] = useState(cardData.comment);
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setComment(e.target.value);
  };
  const [editMemberApprovalRequest] = useMutation(EDIT_MEMBER_APPROVAL_REQUEST);
  const [cardStatus, setCardStatus] = useState<ApprovalRequestType>(
    cardData.state
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const rejectingUser = useAppSelector(
    (state) => state.profileData.profileData?.displayName
  );

  const handleSend = async (): Promise<void> => {
    setIsRejected(false);
    setIsLoading(true);
    try {
      setCardStatus(ApprovalRequestType.REJECTED);

      await editMemberApprovalRequest({
        variables: {
          input: {
            id: cardData.id,
            comment: comment
              ? `${comment}        ( Rejected By : ${rejectingUser as string}) `
              : '',
            action: 'REJECT'
          }
        }
      });
    } catch (error) {
      setCardStatus(ApprovalRequestType.PENDING);
    }
  };

  const resources: ISuggestedAllocations[] =
    cardData.approvalRequest.metadata.suggestedAllocations;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '16px',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid rgba(95, 99, 104, 0.5)',
        gap: '10px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            md: 'row',
            xs: 'column'
          },
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <Box sx={{ width: { md: '70%', xs: '100%' } }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: 'Be Vietnam Pro',
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginBottom: { md: 0, xs: '20px' }
            }}
          >
            {cardData.approvalRequest.description}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: 'Be Vietnam Pro',
            lineHeight: '20px',
            letterSpacing: '0.25px',
            color: '#74777F',
            minWidth: '192px',
            marginLeft: { md: '20px', xs: 'none' }
          }}
        >
          {new Date(cardData.createdAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            md: 'row',
            xs: 'column'
          },
          justifyContent: 'space-between'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '10px',
            flex: 1
          }}
        >
          {resources.length !== 0 && (
            <AllocationTableInPendingAction
              currentAllocationProjects={resources}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginLeft: '20px',
            marginTop: '20px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: resources.length === 0 ? 'row' : 'column',
              alignItems: resources.length === 0 ? 'center' : 'flex-end'
            }}
          >
            {resources.length === 0 && (
              <Box
                sx={{
                  backgroundColor: '#F2F3FB',
                  padding: '7px 15px',
                  borderRadius: '7px',
                  marginRight: '20px'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Be Vietnam Pro',
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                    color: '#001E2F'
                  }}
                >
                  Allocation - {cardData.approvalRequest.metadata.newAllocation}{' '}
                  hours per week
                </Typography>
              </Box>
            )}

            <Tooltip
              title={cardData.resourcesArray[0].displayName}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    background: '#f2f3fb',
                    color: '#37383C',
                    fontSize: '10px',
                    fontWeight: 400
                  }
                },
                arrow: {
                  sx: {
                    color: '#f2f3fb'
                  }
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  marginBottom: resources.length === 0 ? '0' : '10px'
                }}
              >
                <AvatarImage
                  providerId={cardData.resourcesArray[0].providerId}
                  key={cardData.resourcesArray[0].id}
                  fontSize="20px"
                  alt={cardData.resourcesArray[0].displayName}
                  width="50px"
                  height="50px"
                />
              </Box>
            </Tooltip>

            {resources.length !== 0 && (
              <Box
                sx={{
                  backgroundColor: '#F2F3FB',
                  padding: '7px 15px',
                  borderRadius: '7px'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Be Vietnam Pro',
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                    color: '#001E2F'
                  }}
                >
                  Allocation - {cardData.approvalRequest.metadata.newAllocation}{' '}
                  hours per week
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {resources.length === 0 && (
          <Box
            sx={{
              height: '1px',
              background: '#E3E2E699',
              marginBottom: '10px',
              width: '100%'
            }}
          />
        )}
        <Box sx={{ alignSelf: 'flex-end' }}>
          <PendingActionButtonsComponent
            id={cardData.id}
            setIsRejected={(input) => {
              setIsRejected(input);
            }}
            cardStatus={cardStatus}
            isLoading={isLoading}
            setIsLoading={(input) => {
              setIsLoading(input);
            }}
          />
        </Box>
      </Box>
      {cardStatus === ApprovalRequestType.REJECTED && (
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            fontFamily: 'Be Vietnam Pro',
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#1A1B1F'
          }}
        >
          {comment}
        </Typography>
      )}

      {isRejected && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              gap: '12px'
            }}
          >
            <TextField
              fullWidth
              id="outlined-number"
              label="Comment"
              type="text"
              onChange={handleInput}
              value={comment}
              multiline
              InputLabelProps={{
                shrink: true
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        void (async () => {
                          await handleSend();
                        })();
                      }}
                      aria-label="send"
                      sx={{ backgroundColor: 'rgba(103, 80, 164, 0.12)' }}
                    >
                      <SendIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: 'Be Vietnam Pro',
                lineHeight: '24px',
                letterSpacing: '0.5px',
                color: '#1A1B1F',
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default PendingActionCardTypeThree;
